import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, Eye, Coins, CreditCard, AlertTriangle, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useCredits } from "@/hooks/useCredits";
import { useSubscription } from "@/hooks/useSubscription";
import { monitoringApi, MonitoringAlert } from "@/lib/api/monitoring";
import { cn } from "@/lib/utils";

interface Notification {
  id: string;
  type: 'monitoring' | 'credits' | 'subscription';
  title: string;
  message: string;
  link: string;
  priority: 'high' | 'medium' | 'low';
  icon: React.ReactNode;
  dismissible?: boolean;
}

export const NotificationBell = () => {
  const [monitoringAlerts, setMonitoringAlerts] = useState<MonitoringAlert[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const { language } = useLanguage();
  const { credits } = useCredits();
  const { subscription, currentPlan } = useSubscription();

  // Load monitoring alerts
  useEffect(() => {
    const loadAlerts = async () => {
      if (currentPlan && (currentPlan.id === 'pro' || currentPlan.id === 'unlimited')) {
        const alerts = await monitoringApi.getAlerts(true);
        setMonitoringAlerts(alerts);
      }
    };
    loadAlerts();
  }, [currentPlan]);

  // Load dismissed notifications from localStorage
  useEffect(() => {
    const dismissed = localStorage.getItem('dismissed_notifications');
    if (dismissed) {
      setDismissedNotifications(JSON.parse(dismissed));
    }
  }, []);

  const dismissNotification = (id: string) => {
    const newDismissed = [...dismissedNotifications, id];
    setDismissedNotifications(newDismissed);
    localStorage.setItem('dismissed_notifications', JSON.stringify(newDismissed));
  };

  // Build notifications list
  const buildNotifications = (): Notification[] => {
    const notifications: Notification[] = [];

    // Monitoring alerts
    monitoringAlerts.forEach((alert) => {
      notifications.push({
        id: `monitoring-${alert.id}`,
        type: 'monitoring',
        title: language === 'fr' ? 'Alerte monitoring' : 'Monitoring alert',
        message: alert.message,
        link: '/account?tab=monitoring',
        priority: 'high',
        icon: <Eye className="w-4 h-4 text-primary" />,
      });
    });

    // Low credits warning (10 or less)
    if (credits && credits.credits_balance <= 10 && credits.credits_balance > 0) {
      const id = `credits-low-${credits.credits_balance}`;
      if (!dismissedNotifications.includes(id)) {
        notifications.push({
          id,
          type: 'credits',
          title: language === 'fr' ? 'Crédits faibles' : 'Low credits',
          message: language === 'fr' 
            ? `Il vous reste ${credits.credits_balance} crédit${credits.credits_balance > 1 ? 's' : ''}`
            : `You have ${credits.credits_balance} credit${credits.credits_balance > 1 ? 's' : ''} left`,
          link: '/pricing',
          priority: credits.credits_balance <= 3 ? 'high' : 'medium',
          icon: <Coins className="w-4 h-4 text-warning" />,
          dismissible: true,
        });
      }
    }

    // No credits warning
    if (credits && credits.credits_balance === 0) {
      const id = 'credits-zero';
      if (!dismissedNotifications.includes(id)) {
        notifications.push({
          id,
          type: 'credits',
          title: language === 'fr' ? 'Plus de crédits' : 'Out of credits',
          message: language === 'fr' 
            ? 'Rechargez pour continuer à analyser'
            : 'Top up to continue analyzing',
          link: '/pricing',
          priority: 'high',
          icon: <AlertTriangle className="w-4 h-4 text-destructive" />,
          dismissible: true,
        });
      }
    }

    // Payment failed warning
    if (subscription?.status === 'payment_failed' || subscription?.status === 'suspended') {
      const id = `payment-failed-${subscription.status}`;
      if (!dismissedNotifications.includes(id)) {
        const isSuspended = subscription.status === 'suspended';
        notifications.push({
          id,
          type: 'subscription',
          title: language === 'fr' 
            ? (isSuspended ? 'Compte suspendu' : 'Échec de paiement')
            : (isSuspended ? 'Account suspended' : 'Payment failed'),
          message: language === 'fr'
            ? (isSuspended 
              ? 'Votre compte est suspendu. Régularisez votre paiement pour le réactiver.'
              : 'Votre paiement a échoué. Régularisez sous 24h pour éviter la suspension.')
            : (isSuspended
              ? 'Your account is suspended. Update your payment to reactivate.'
              : 'Your payment failed. Update within 24h to avoid suspension.'),
          link: '/account?tab=credits',
          priority: 'high',
          icon: <AlertTriangle className="w-4 h-4 text-destructive" />,
        });
      }
    }

    // Subscription expiring warnings
    if (subscription?.current_period_end && subscription.plan !== 'free') {
      const endDate = new Date(subscription.current_period_end);
      const now = new Date();
      const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      const warningThresholds = [
        { days: 30, key: '1month', labelFr: '1 mois', labelEn: '1 month' },
        { days: 10, key: '10days', labelFr: '10 jours', labelEn: '10 days' },
        { days: 7, key: '1week', labelFr: '1 semaine', labelEn: '1 week' },
        { days: 2, key: '48h', labelFr: '48 heures', labelEn: '48 hours' },
        { days: 1, key: '24h', labelFr: '24 heures', labelEn: '24 hours' },
      ];

      // Find the appropriate warning threshold
      for (const threshold of warningThresholds) {
        if (daysRemaining <= threshold.days && daysRemaining > 0) {
          const id = `subscription-expiring-${threshold.key}`;
          if (!dismissedNotifications.includes(id)) {
            const isUrgent = daysRemaining <= 2;
            notifications.push({
              id,
              type: 'subscription',
              title: language === 'fr' ? 'Abonnement bientôt terminé' : 'Subscription ending soon',
              message: language === 'fr' 
                ? `Votre abonnement expire dans ${threshold.labelFr}`
                : `Your subscription expires in ${threshold.labelEn}`,
              link: '/account?tab=credits',
              priority: isUrgent ? 'high' : 'medium',
              icon: <CreditCard className={cn("w-4 h-4", isUrgent ? "text-destructive" : "text-warning")} />,
              dismissible: true,
            });
          }
          break; // Only show the most relevant threshold
        }
      }

      // Subscription expired
      if (daysRemaining <= 0) {
        const id = 'subscription-expired';
        if (!dismissedNotifications.includes(id)) {
          notifications.push({
            id,
            type: 'subscription',
            title: language === 'fr' ? 'Abonnement expiré' : 'Subscription expired',
            message: language === 'fr' 
              ? 'Renouvelez pour conserver votre accès'
              : 'Renew to keep your access',
            link: '/pricing',
            priority: 'high',
            icon: <AlertTriangle className="w-4 h-4 text-destructive" />,
            dismissible: true,
          });
        }
      }
    }

    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return notifications.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  };

  const notifications = buildNotifications();
  const hasNotifications = notifications.length > 0;
  const highPriorityCount = notifications.filter(n => n.priority === 'high').length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-9 w-9"
          aria-label={language === 'fr' ? 'Notifications' : 'Notifications'}
        >
          <Bell className={cn(
            "w-5 h-5 transition-colors",
            hasNotifications ? "text-primary" : "text-muted-foreground"
          )} />
          {hasNotifications && (
            <span className={cn(
              "absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white",
              highPriorityCount > 0 ? "bg-destructive" : "bg-primary"
            )}>
              {notifications.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b border-border">
          <h4 className="font-semibold text-sm">
            {language === 'fr' ? 'Notifications' : 'Notifications'}
          </h4>
        </div>
        
        {notifications.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
            {language === 'fr' ? 'Aucune notification' : 'No notifications'}
          </div>
        ) : (
          <ScrollArea className="max-h-[300px]">
            <div className="divide-y divide-border">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "p-3 hover:bg-muted/50 transition-colors relative group",
                    notification.priority === 'high' && "bg-destructive/5"
                  )}
                >
                  <Link 
                    to={notification.link}
                    onClick={() => setOpen(false)}
                    className="flex items-start gap-3"
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                      notification.priority === 'high' ? "bg-destructive/10" : "bg-muted"
                    )}>
                      {notification.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {notification.title}
                        </span>
                        {notification.priority === 'high' && (
                          <Badge variant="destructive" className="text-[10px] px-1 py-0">
                            {language === 'fr' ? 'Urgent' : 'Urgent'}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />
                  </Link>
                  
                  {notification.dismissible && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        dismissNotification(notification.id);
                      }}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
        
        {notifications.length > 0 && (
          <div className="p-2 border-t border-border">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-xs"
              asChild
            >
              <Link to="/account" onClick={() => setOpen(false)}>
                {language === 'fr' ? 'Voir mon compte' : 'View my account'}
              </Link>
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};